import { inject, Injectable } from "@angular/core";
import { ConfirmModalService } from "@core-services/confirm-modal.service";
import { InfoService } from "@core-services/info.service";
import { ModalService } from "@core-services/modal.service";
import { FEAT_GATES, StatsigClientService } from "@core-services/statsig/statsig.service";
import { TranslationService } from "@core-services/translation.service";
import { Helper } from "@helpers/functions";
import { SegmentService } from "@main-services/segment.service";
import { SendTransaction } from "@models/transaction";
import { Network } from "@modules/+new/payments/features/crypto-deposit/domain/entities/network.model";
import { PaymentsCryptocoin } from "@modules/+new/payments/shared-payments/entities/payments-cryptocoin.model";
import { CryptocoinsState } from "@modules/+new/payments/shared-payments/services/cryptocoins-state.service";
import { BpSelectOption } from "@modules/core/components/bp-select/bp-select.component";
import { AddressBookCreateOwnerTypeModalComponent } from "@modules/main/components/address-book-wallet/address-book-create-owner-type-modal/address-book-create-owner-type-modal.component";
import { AddressBookCreateWalletModalComponent } from "@modules/main/components/address-book-wallet/address-book-create-wallet-modal/address-book-create-wallet-modal.component";
import { ProfileModel } from "@modules/main/components/profile/new-profile/entities/profile-model";
import { NewProfileFacade } from "@modules/main/components/profile/new-profile/state/new-profile.facade";
import { SendAmountComponent } from "@modules/main/components/step-summary/sending-modal/send-amount/send-amount.component";
import { SendTrackingFacade } from "@modules/main/components/step-summary/sending-modal/services/send-tracking.facade";
import { BehaviorSubject, combineLatest, Observable, of } from "rxjs";
import { catchError, map, switchMap, take } from "rxjs/operators";
import { AddressBookViewWalletModalComponent } from "../address-book-view-wallet-modal/address-book-view-wallet-modal.component";
import {
	AddressBookWalletHostModel,
	AddressBookWalletModel,
	AddressBookWalletViewType,
	OwnerType
} from "../model/address-book-wallet.model";
import { AddressBookWalletService } from "./address-book-wallet.service";
import { RudderstackService } from "@main-services/rudderstack.service";
import { TinShieldEntryHelper } from "@modules/taxes/tin/services/tin-shield-entry.helper";
import { AddressBookCreateWalletTypeModalComponent } from "../address-book-create-wallet-type-modal/address-book-create-wallet-type-modal.component";

@Injectable({ providedIn: "root" })
export class AddressBookWalletFacade {
	public onBehalfOf: BehaviorSubject<string> = new BehaviorSubject<string>("");
	private readonly tinShieldEntryHelper = inject(TinShieldEntryHelper);
	constructor(
		private readonly cryptoCoinsState: CryptocoinsState,
		private readonly walletService: AddressBookWalletService,
		private readonly profileFacade: NewProfileFacade,
		private readonly modalService: ModalService,
		private readonly confirmModalService: ConfirmModalService,
		private readonly infoService: InfoService,
		private readonly translationService: TranslationService,
		private readonly trackingFacade: SendTrackingFacade,
		private readonly segmentService: SegmentService,
		private readonly rudderstackService: RudderstackService,
		private readonly statsigService: StatsigClientService
	) {}

	public getAssetOptions(): Observable<BpSelectOption[]> {
		return this.cryptoCoinsState.sortedCryptoListForContacts$.pipe(
			map((cryptoList: PaymentsCryptocoin[]) =>
				cryptoList
					.filter(({ is_withdraw_allowed }: PaymentsCryptocoin) => is_withdraw_allowed)
					.map((crypto: PaymentsCryptocoin) => {
						const assetOption = new BpSelectOption();
						assetOption.value = crypto.id.toString();
						assetOption.name = crypto.name[0].toUpperCase() + crypto.name.substring(1).toLowerCase();
						assetOption.iconUrl = crypto.logo;
						assetOption.details = crypto.cryptocoin_id.toString();
						return assetOption;
					})
			)
		);
	}

	public getWalletHostsOptions(): Observable<BpSelectOption[]> {
		return this.walletService.getWalletHosts().pipe(
			map((hosts: AddressBookWalletHostModel[]) =>
				hosts.map((host: AddressBookWalletHostModel) => ({
					name: host.wallet_host_name,
					value: host.wallet_host_name,
					wallet_host_id: host.wallet_host_id
				}))
			)
		);
	}

	public getNetworksForSelectedCoin(coinId: string): Observable<BpSelectOption[]> {
		return this.cryptoCoinsState.cryptocoins.pipe(
			map((cryptoList: PaymentsCryptocoin[]) => {
				const crypto: PaymentsCryptocoin = cryptoList.find((cr: PaymentsCryptocoin) => cr.id === coinId);
				if (crypto) {
					return crypto.networks
						.filter(({ is_withdraw_allowed }: Network) => is_withdraw_allowed)
						.map((network: Network) => {
							let networkOption = new BpSelectOption();
							networkOption.value = network.coin_network_id;
							networkOption.name =
								network.name[0].toUpperCase() + network.name.substring(1).toLowerCase();
							networkOption.iconUrl = network.logo;
							networkOption.details = network.destination_tag ? network.destination_tag.type : null;
							return networkOption;
						});
				}
				return [];
			})
		);
	}

	public getWallets(): Observable<AddressBookWalletModel[]> {
		return this.profileFacade.getProfile$().pipe(
			switchMap(({ fullName }: ProfileModel) =>
				this.walletService.getWallets().pipe(
					map((wallets: AddressBookWalletModel[]) => {
						return wallets.map((wallet: AddressBookWalletModel) => {
							if (wallet.user_owned && !wallet.owner_legal_name) {
								return { ...wallet, owner_legal_name: fullName };
							}
							return wallet;
						});
					})
				)
			)
		);
	}

	public updateOwnerName(wallet: AddressBookWalletModel): AddressBookWalletModel {
		this.profileFacade
			.getProfile$()
			.pipe(
				map(({ fullName }: ProfileModel) => {
					return fullName;
				})
			)
			.subscribe((fullName) => {
				wallet.owner_legal_name = fullName;
			});
		return wallet;
	}

	public getCoinByCryptocoinName(name: string): Observable<PaymentsCryptocoin> {
		return this.cryptoCoinsState.sortedCryptoListForContacts$.pipe(
			map((list) => {
				return list.find((item) => item.name.toString() === name);
			})
		);
	}

	public displayViewWalletModal(wallet: AddressBookWalletModel): void {
		this.segmentService.track({
			name: "Address Book Add Wallet Confirmed Screen Viewed"
		});
		this.rudderstackService.track({
			name: "Address Book Add Wallet Confirmed Screen Viewed"
		});

		this.modalService.showModal(AddressBookViewWalletModalComponent, wallet);
	}

	public displayCreateWalletModal(
		type: AddressBookWalletViewType,
		wallet?: AddressBookWalletModel,
		accountWalletId?: string,
		selectedNetwork?: Network,
		selectedCryptocoin?: PaymentsCryptocoin
	): void {
		combineLatest({
			isBusinessFlowEnabled: this.statsigService.isFeatureActive(FEAT_GATES.TRAVEL_RULE_BUSINESS_FLOW),
			isTravelRuleV2Enabled: this.statsigService.isFeatureActive(FEAT_GATES.TRAVEL_RULE_V2),
			SHW3rdPartyDisabled: this.statsigService.isFeatureActive(FEAT_GATES.SHW_3RD_PARTY_DISABLED)
		}).subscribe(({ isBusinessFlowEnabled, isTravelRuleV2Enabled, SHW3rdPartyDisabled }) => {
			if (isTravelRuleV2Enabled) {
				if (isBusinessFlowEnabled) {
					// Show the new business flow modal with proper data
					this.modalService.showModal(AddressBookCreateOwnerTypeModalComponent, {
						type,
						wallet,
						accountWalletId,
						selectedNetwork,
						selectedCryptocoin,
						SHW3rdPartyDisabled
					});
				} else {
					// Show V2 flow modal hardcoded for PERSON owner type
					this.modalService.showModal(AddressBookCreateWalletTypeModalComponent, {
						type,
						wallet,
						accountWalletId,
						selectedNetwork,
						selectedCryptocoin,
						ownerType: OwnerType.PERSON,
						SHW3rdPartyDisabled
					});
				}
			} else {
				// Show the original create wallet modal
				this.modalService.showModal(AddressBookCreateWalletModalComponent, {
					type,
					wallet,
					accountWalletId,
					selectedNetwork,
					selectedCryptocoin
				});
			}
		});
	}

	public deleteWalletById(id: string): void {
		this.confirmModalService
			.showConfirmModal("delete_wallet", "delete_wallet_description", "delete_wallet", null)
			.pipe(
				take(1),
				switchMap((yes: boolean) => {
					if (yes) {
						return this.walletService.deleteWallet(id).pipe(
							catchError((error) => {
								this.infoService.setError(error);
								return of(false);
							}),
							map(() => of(true))
						);
					}
					return of(false);
				})
			)
			.subscribe((result: boolean) => {
				if (result) {
					this.modalService.popAll();
					this.infoService.setSuccess(this.translationService.translate("wallet_deleted"));
					this.walletService.shouldRefreshWallets.next(true);
				}
			});
	}

	public validateWalletToSend(
		accountWalletId: string,
		wallet: AddressBookWalletModel,
		selectedNetwork: Network,
		selectedCryptocoin: PaymentsCryptocoin
	): void {
		this.tinShieldEntryHelper
			.handleTinStatusCheck$()
			.pipe(take(1))
			.subscribe({
				next: (isTinFrozen) => {
					if (isTinFrozen) {
						this.modalService.popAll();
						return;
					}
					this.trackingFacade.trackRecipientChosen();
					const sendTransaction = new SendTransaction();
					sendTransaction.setWalletId(accountWalletId);
					sendTransaction.setRecipient(wallet.wallet_address);
					sendTransaction.setDestinationTag(wallet.wallet_destination_tag);
					sendTransaction.setSendType("external");
					sendTransaction._try_internal = false;
					sendTransaction.setIsExternal(true);
					sendTransaction.coin_network_id = wallet.network.coin_network_id;
					sendTransaction.setAddressBookWalletId(wallet.id);
					this.walletService.validateContactAndGetFee(accountWalletId, sendTransaction).subscribe({
						next: (res) => {
							const modal = this.modalService.create(SendAmountComponent, {
								selectedNetwork: selectedNetwork,
								selectedCryptocoin: selectedCryptocoin,
								sendTransaction,
								contactName: wallet.owner_legal_name,
								fee: res,
								selectedHost: wallet.wallet_host_name,
								legalEntityIdentifier: wallet.legal_entity_identifier || ""
							});
							modal.present();
						},
						error: (error) => {
							Helper.handleError(error, sendTransaction, { forceToast: true });
						}
					});
				}
			});
	}
}
